import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Printer, Download, FileText } from "lucide-react";
import { toast } from "sonner";

// Reusable Print / Download (HTML) / PDF button trio.
// - htmlSource: (preferred) raw HTML string to render for every export type — used by the
//   Request Detail printable so PDF/Download always match the on-print layout exactly.
// - targetRef: legacy fallback for the DOM ref to snapshot when htmlSource is not provided.
export default function PrintExportButtons({
  targetRef,
  htmlSource,
  fileName = "out-and-in-document",
  size = "sm",
  testidPrefix = "export",
  label = "",
}) {
  const [busy, setBusy] = useState(false);
  const btnH = size === "md" ? "h-10" : "h-9";

  const getNode = () => (targetRef && targetRef.current) || document.body;

  // Build a standalone HTML document string (for print window + HTML download).
  const buildStandaloneHtml = () => {
    const body = htmlSource || getNode().outerHTML;
    const styles = Array.from(document.querySelectorAll('link[rel="stylesheet"], style'))
      .map((n) => n.outerHTML)
      .join("");
    return `<!doctype html><html><head><meta charset="utf-8"><title>${fileName}</title>${styles}</head><body>${body}</body></html>`;
  };

  const doPrint = () => {
    if (htmlSource) {
      // Open a dedicated print window with the exact email HTML — matches PDF output byte-for-byte.
      const win = window.open("", "_blank", "width=900,height=1000");
      if (!win) return window.print();
      win.document.open();
      win.document.write(buildStandaloneHtml());
      win.document.close();
      // Wait for any inline images (logo) before printing.
      const trigger = () => { try { win.focus(); win.print(); } catch {} };
      const imgs = win.document.images;
      if (imgs.length === 0) setTimeout(trigger, 200);
      else {
        let done = 0;
        const check = () => { done++; if (done >= imgs.length) setTimeout(trigger, 200); };
        Array.from(imgs).forEach((img) => {
          if (img.complete) check();
          else { img.addEventListener("load", check); img.addEventListener("error", check); }
        });
      }
      return;
    }
    window.print();
  };

  const doDownload = () => {
    try {
      const html = buildStandaloneHtml();
      const blob = new Blob([html], { type: "text/html;charset=utf-8" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${fileName}.html`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 500);
      toast.success("Download started", { description: `${fileName}.html` });
    } catch (e) {
      toast.error("Download failed");
    }
  };

  const doPdf = async () => {
    setBusy(true);
    let stage = null;
    try {
      const html2pdf = (await import("html2pdf.js")).default;

      let target;
      if (htmlSource) {
        // Build a self-contained white-background container. html2pdf handles the rest.
        stage = document.createElement("div");
        stage.style.width = "800px";
        stage.style.background = "#ffffff";
        stage.style.padding = "0";
        stage.innerHTML = htmlSource;
        // Append to a hidden wrapper so it renders with real dimensions.
        const wrap = document.createElement("div");
        wrap.style.position = "fixed";
        wrap.style.top = "0";
        wrap.style.left = "0";
        wrap.style.visibility = "hidden"; // hidden to the user, but html2canvas can still render it
        wrap.style.pointerEvents = "none";
        wrap.appendChild(stage);
        document.body.appendChild(wrap);
        stage._wrap = wrap; // remember for cleanup
        await new Promise((r) => requestAnimationFrame(() => r()));
        await Promise.all(
          Array.from(stage.querySelectorAll("img")).map(
            (img) =>
              img.complete
                ? Promise.resolve()
                : new Promise((r) => { img.addEventListener("load", r); img.addEventListener("error", r); }),
          ),
        );
        target = stage;
      } else {
        target = getNode();
      }

      await html2pdf()
        .from(target)
        .set({
          margin: [10, 10],
          filename: `${fileName}.pdf`,
          html2canvas: { scale: 2, useCORS: true, backgroundColor: "#ffffff" },
          jsPDF: { unit: "mm", format: "a4", orientation: "portrait" },
          pagebreak: { mode: ["css", "legacy"] },
        })
        .save();
      toast.success("PDF ready", { description: `${fileName}.pdf` });
    } catch (e) {
      toast.error("PDF export failed");
    } finally {
      if (stage && stage._wrap && stage._wrap.parentNode) {
        stage._wrap.parentNode.removeChild(stage._wrap);
      }
      setBusy(false);
    }
  };

  return (
    <div className="flex items-center gap-2 no-print" data-testid={`${testidPrefix}-toolbar`}>
      {label && <span className="hidden sm:inline text-[10px] uppercase tracking-[0.2em] font-bold text-muted-foreground">{label}</span>}
      <Button
        type="button"
        onClick={doPrint}
        variant="outline"
        data-testid={`${testidPrefix}-print`}
        className={`rounded-none border-2 ${btnH} uppercase tracking-[0.15em] text-xs font-bold`}
      >
        <Printer className="h-3.5 w-3.5 mr-2" /> Print
      </Button>
      <Button
        type="button"
        onClick={doDownload}
        variant="outline"
        data-testid={`${testidPrefix}-download`}
        className={`rounded-none border-2 ${btnH} uppercase tracking-[0.15em] text-xs font-bold`}
      >
        <Download className="h-3.5 w-3.5 mr-2" /> Download
      </Button>
      <Button
        type="button"
        onClick={doPdf}
        disabled={busy}
        data-testid={`${testidPrefix}-pdf`}
        className={`rounded-none bg-orange-600 hover:bg-orange-700 text-white ${btnH} uppercase tracking-[0.15em] text-xs font-bold`}
      >
        <FileText className="h-3.5 w-3.5 mr-2" /> {busy ? "PDF…" : "PDF"}
      </Button>
    </div>
  );
}
